<template>
    <div class="form-group">
        <label class="form-label section-title">{{ $t('messages.device_manufacturers_and_os') }}</label>
        <div class="bordered-container">
            <div class="segments-tabs">
                <button v-for="(group, index) in groups" :key="group.id"
                    :class="['tab-button', { active: activeGroupIndex === index }]" @click="switchGroup(index)">
                    {{ group.title }}
                    <span class="filter-count">{{ getSelectedCount(group) }}</span>
                </button>
            </div>

            <div class="controls-panel">
                <div class="search-input">
                    <img src="/images/search.svg" alt="search" class="search-icon" />
                    <input type="text" class="form-input" v-model="searchQuery"
                        :placeholder="$t('messages.search_segments_placeholder')" />
                    <button v-if="searchQuery" class="clear-search" @click="clearSearch">
                        <img src="/images/close.svg" alt="clear" />
                    </button>
                </div>
            </div>

            <div class="segments-content">
                <div class="segments-column">
                    <div class="segments-list">
                        <SegmentItem v-for="segment in filteredSegments" :key="segment.id" :segment="segment" :level="0"
                            :expanded-items="expandedItems" :selected="currentSelectedIds"
                            :should-show-description="false" @add-segment="addSegment" @remove-segment="removeSegment"
                            @toggle-expand="toggleExpand" />
                        <div v-if="filteredSegments.length === 0" class="empty-state">
                            {{ $t('messages.no_available_segments') }}
                        </div>
                    </div>
                </div>

                <div class="selected-column">
                    <div class="selected-header">
                        <div class="selected-label">{{ $t('messages.selected') }}</div>
                        <button class="btn-delete-all" @click="deleteAll">
                            {{ $t('messages.delete_all') }}
                        </button>
                    </div>
                    <div class="selected-content">
                        <div v-for="segment in currentSelectedSegments" :key="segment.id" class="selected-segment">
                            <span class="segment-name">{{ segment.title }}</span>
                            <button class="remove-btn" @click="removeSegment(segment)">
                                <img src="/images/circle-minus.svg" alt="remove" />
                            </button>
                        </div>
                        <div v-if="currentSelectedSegments.length === 0" class="empty-state">
                            {{ $t('messages.no_selected_segments') }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import SegmentItem from '../ReachBrandSafety/SegmentItem.vue';

export default {
    name: "DeviceSegments",
    components: { SegmentItem },
    props: {
        operatingSystems: {
            type: Array,
            default: () => []
        },
        selected: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            searchQuery: '',
            expandedItems: new Set(),
            activeGroupIndex: 0,
            groups: [] // will be built from operatingSystems
        };
    },
    watch: {
        operatingSystems: {
            handler() { this.buildGroups(); },
            immediate: true,
            deep: true
        }
    },
    computed: {
        currentSelectedIds() {
            return this.selected || [];
        },
        currentGroup() {
            return this.groups[this.activeGroupIndex] || null;
        },
        filteredSegments() {
            if (!this.currentGroup) return [];
            let list = this.currentGroup.children && this.currentGroup.children.length ? this.currentGroup.children : [this.currentGroup];
            if (this.searchQuery) {
                list = this.filterSegmentsRecursively(list, this.searchQuery.toLowerCase());
            }
            return list;
        },
        currentSelectedSegments() {
            const ids = this.currentSelectedIds.map(id => parseInt(id));
            const result = [];
            const findIn = (segments) => {
                for (const s of segments) {
                    if (ids.includes(s.id)) result.push(s);
                    if (s.children && s.children.length) findIn(s.children);
                }
            };
            if (this.currentGroup) {
                const list = this.currentGroup.children && this.currentGroup.children.length ? this.currentGroup.children : [this.currentGroup];
                findIn(list);
            }
            return result;
        }
    },
    methods: {
        buildGroups() {
            const items = this.operatingSystems || [];
            const map = new Map();
            items.forEach(it => {
                map.set(it.id, { id: it.id, parent_id: it.parent_id || null, title: it.operating_system || it.name || '', description: it.description || '', children: [] });
            });
            // attach children
            map.forEach((v, id) => {
                if (v.parent_id && map.has(v.parent_id)) {
                    map.get(v.parent_id).children.push(v);
                }
            });
            // group roots: manufacturers (3), os (2), browser (1)
            const roots = [];
            [3, 2, 1].forEach(rootId => {
                if (map.has(rootId)) roots.push(map.get(rootId));
            });
            this.groups = roots;
            if (this.groups.length === 0 && items.length > 0) {
                // fallback: build roots from null parents
                const fallback = [];
                map.forEach(v => { if (!v.parent_id) fallback.push(v); });
                this.groups = fallback;
            }
        },
        switchGroup(index) {
            this.activeGroupIndex = index;
            this.searchQuery = '';
            this.expandedItems.clear();
        },
        addSegment(segment) {
            const ids = [...this.currentSelectedIds];
            if (!ids.includes(segment.id)) {
                ids.push(segment.id);
                this.$emit('update-devices', ids);
            }
        },
        removeSegment(segment) {
            const ids = [...this.currentSelectedIds];
            const idx = ids.findIndex(id => id === segment.id || id === parseInt(segment.id));
            if (idx > -1) {
                ids.splice(idx, 1);
                this.$emit('update-devices', ids);
            }
        },
        deleteAll() {
            this.$emit('update-devices', []);
        },
        toggleExpand(item) {
            if (this.expandedItems.has(item.id)) this.expandedItems.delete(item.id);
            else this.expandedItems.add(item.id);
        },
        clearSearch() {
            this.searchQuery = '';
            this.expandedItems.clear();
        },
        filterSegmentsRecursively(segments, query) {
            const result = [];
            for (const segment of segments) {
                const matchesTitle = segment.title && segment.title.toLowerCase().includes(query);
                let matchingChildren = [];
                if (segment.children && segment.children.length > 0) {
                    matchingChildren = this.filterSegmentsRecursively(segment.children, query);
                }
                if (matchesTitle || matchingChildren.length > 0) {
                    const filteredSegment = { ...segment };
                    if (matchingChildren.length > 0) {
                        filteredSegment.children = matchingChildren;
                        this.expandedItems.add(segment.id);
                    }
                    result.push(filteredSegment);
                }
            }
            return result;
        },
        getSelectedCount(group) {
            const ids = this.currentSelectedIds.map(id => parseInt(id));
            let count = 0;
            const walk = (items) => {
                for (const it of items) {
                    if (ids.includes(it.id)) count++;
                    if (it.children && it.children.length) walk(it.children);
                }
            };
            if (!group) return 0;
            if (group.children && group.children.length) walk(group.children);
            else if (ids.includes(group.id)) count++;
            return count;
        }
    }
};
</script>

<style lang="scss" scoped>
/* reuse AudienceSegments styles where applicable */
.bordered-container {
    border: 1px solid #e9ecef;
    border-radius: 8px;
    background: white;
    overflow: hidden;
}

.segments-tabs {
    display: flex;
    border-bottom: 1px solid #e9ecef;
    background: #f8f9fa;
}

.segments-content {
    display: flex;
    gap: 0;
    min-height: 320px;
}

.segments-column {
    flex: 0 0 70%;
    border-right: 1px solid #e9ecef;
}

.selected-column {
    flex: 0 0 30%;
    background: #f8f9fa;
    display: flex;
    flex-direction: column;
}

.selected-header {
    padding: 12px;
    border-bottom: 1px solid #e9ecef;
    background: white;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.selected-content {
    flex: 1;
    padding: 16px;
    overflow-y: auto;
}

.selected-segment {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    background: white;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    padding: 8px 12px;
    margin-bottom: 8px;
    font-size: 14px;
}

.segment-name {
    color: #495057;
    flex: 1;
    word-break: break-word;
}

.empty-state {
    color: #6c757d;
    font-style: italic;
    font-size: 14px;
    text-align: center;
    padding: 20px;
}

.tab-button {
    flex: 1;
    padding: 12px 16px;
    background: none;
    border: none;
    border-bottom: 3px solid transparent;
    font-size: 14px;
    font-weight: 500;
    color: #6c757d;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.tab-button.active {
    color: #ec4899;
    border-bottom-color: #ec4899;
    background: white;
}

.filter-count {
    background: #6c757d;
    color: white;
    border-radius: 12px;
    padding: 2px 8px;
    font-size: 12px;
    font-weight: 600;
    min-width: 20px;
    text-align: center;
}

.tab-button.active .filter-count {
    background: #ec4899;
}
</style>
